import { printEncounter } from "../../lib/encounterPrint";
import { ScheduleFollowupModal } from "../../components/ScheduleFollowupModal";
import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DashboardLayout } from "../../components/DashboardLayout";
import api from "../../lib/api";
import { toast } from "sonner";
import { useCan } from "../../lib/permissions";
import { useAuth } from "../../lib/auth";
import { useEncounterSave } from "../../lib/useEncounterSave";
import { draftKey, readDraft, rememberDraft, forgetDraft } from "../../lib/encounterDrafts";
import { PatientTagPicker } from "../clinicadmin/PatientsModulePages";
import { PrescriptionLineEditor } from "../../components/PrescriptionLineEditor";
import { PharmacyProductPicker } from "../../components/PharmacyProductPicker";
import { Printer, XCircle, ChevronLeft, Plus, Image as ImageIcon, Trash2, Clock, CheckCircle, Activity, FileText, ArrowRight, User, Calendar, Phone } from "lucide-react";

const VITAL_FIELDS = [
  { key: "bp", aliases: ["blood_pressure"], label: "Blood pressure", unit: "mmHg", placeholder: "120/80", type: "text", inputMode: "text" },
  { key: "pulse", aliases: ["heart_rate"], label: "Pulse", unit: "bpm", placeholder: "72", type: "number", min: "0", step: "1" },
  { key: "temp", aliases: ["temperature"], label: "Temperature", unit: "°F", placeholder: "98.6", type: "number", min: "0", step: "0.1" },
  { key: "respiration", aliases: ["respiratory_rate"], label: "Respiration", unit: "breaths/min", placeholder: "16", type: "number", min: "0", step: "1" },
  { key: "spo2", aliases: ["oxygen_saturation"], label: "SpO₂", unit: "%", placeholder: "98", type: "number", min: "0", max: "100", step: "1" },
  { key: "weight", aliases: [], label: "Weight", unit: "kg", placeholder: "70", type: "number", min: "0", step: "0.1" },
  { key: "height", aliases: [], label: "Height", unit: "cm", placeholder: "175", type: "number", min: "0", step: "0.1" },
];

function normalizeEncounterVitals(vitals = {}) {
  const normalized = { ...vitals };
  VITAL_FIELDS.forEach(({ key, aliases }) => {
    if (normalized[key] === undefined || normalized[key] === null) {
      const alias = aliases.find((name) => normalized[name] !== undefined && normalized[name] !== null);
      if (alias) normalized[key] = normalized[alias];
    }
    aliases.forEach((name) => delete normalized[name]);
  });
  return normalized;
}

function MedicationAutocomplete({ value, onChange, onSelect }) {
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const query = String(value || "").trim();
    if (query.length < 2) { setOptions([]); return undefined; }
    const timer = setTimeout(() => {
      api.get("/medications", { params: { q: query, limit: 8 } })
        .then((response) => { setOptions(response.data || []); setOpen(true); })
        .catch(() => setOptions([]));
    }, 180);
    return () => clearTimeout(timer);
  }, [value]);
  return <div className="relative min-w-[180px]">
    <input className="mg-input py-1 text-sm w-full" placeholder="Start typing a medicine" value={value || ""}
      onChange={(event) => { onChange(event.target.value); setOpen(true); }} onFocus={() => options.length && setOpen(true)}
      onBlur={() => setTimeout(() => setOpen(false), 120)} autoComplete="off" aria-label="Medication name" />
    {open && options.length > 0 && <div className="absolute z-30 mt-1 max-h-52 w-full overflow-auto rounded-lg border border-slate-200 bg-white shadow-xl">
      {options.map((option) => <button key={option.medication_id} type="button"
        className="block w-full px-3 py-2 text-left text-xs hover:bg-blue-50"
        onMouseDown={(event) => event.preventDefault()} onClick={() => { onSelect(option); setOpen(false); }}>
        <span className="block font-semibold text-mg-navy">{option.name}</span>
        <span className="text-slate-500">{[option.default_strength, option.default_dosage, option.default_frequency].filter(Boolean).join(" · ") || "Clinic medication"}</span>
      </button>)}
    </div>}
  </div>;
}

export function pharmacySelectionDeadline(value) {
  if (typeof value !== "string" || !value.trim()) return Number.NaN;
  const timestamp = value.trim();
  const includesTimezone = /(?:z|[+-]\d{2}:?\d{2})$/i.test(timestamp);
  // PyMongo normally decodes server-owned BSON UTC datetimes without tzinfo,
  // so FastAPI can emit an ISO value with no suffix. Treat that value as UTC.
  return Date.parse(includesTimezone ? timestamp : `${timestamp}Z`);
}

export function isCurrentTypedPrescription(prescription) {
  const lines = prescription?.items || prescription?.medicines || [];
  return prescription?.pharmacy_submission_state === "ready" && lines.length > 0 && lines.every((item) => {
    const line = item?.pharmacy_line;
    return line?.selection_id && line?.connection_id && pharmacySelectionDeadline(line?.selection_expires_at) > Date.now();
  });
}

const pharmacyDeliveryCopy = {
  queued: "Queued for pharmacy delivery.",
  retrying: "Delivery will retry automatically.",
  checking: "Checking delivery status.",
  delivered: "Delivered to the pharmacy.",
  needs_attention: "Delivery needs clinic attention.",
};

function SavedPrescriptionGroup({ prescription, canSend, onSend, sending, submission }) {
  const items = prescription.items || prescription.medicines || [];
  const ready = isCurrentTypedPrescription(prescription);
  return <section className="rounded-lg border border-slate-200 p-4" data-testid={`saved-prescription-${prescription.prescription_id}`}>
    <div className="flex flex-wrap items-start justify-between gap-3"><div><h5 className="font-semibold text-mg-navy">Saved prescription</h5><p className="text-xs text-slate-500">{prescription.created_at ? new Date(prescription.created_at).toLocaleString() : "Saved"}</p></div>{ready && canSend && <button type="button" className="mg-btn-primary text-xs" onClick={() => onSend(prescription)} disabled={sending}> {sending ? "Sending…" : "Send to pharmacy"}</button>}</div>
    <ul className="mt-3 space-y-2">{items.map((item, index) => <li key={`${prescription.prescription_id}-${index}`} className="rounded bg-slate-50 px-3 py-2 text-sm"><span className="font-medium text-mg-navy">{item.medicine_name}</span>{item.strength && <span className="text-slate-500"> · {item.strength}</span>}<div className="text-xs text-slate-600">{[item.dosage, item.frequency, item.duration, item.timing, item.instructions].filter(Boolean).join(" · ")}</div>{item.pharmacy_line && <div className="mt-1 text-xs font-semibold text-blue-700">Dispense: {item.pharmacy_line.quantity} {item.pharmacy_line.unit_name || "base units"}{item.pharmacy_line.lot_number ? ` · Lot ${item.pharmacy_line.lot_number}` : ""}{item.pharmacy_line.expiry_date ? ` · Expires ${item.pharmacy_line.expiry_date}` : ""}</div>}</li>)}</ul>
    {!ready && <p className="mt-3 text-xs text-slate-500">This prescription is saved for the clinical record and is not ready to send to pharmacy.</p>}
    {submission?.delivery_status && <p className="mt-3 text-xs text-slate-600" aria-live="polite">{pharmacyDeliveryCopy[submission.delivery_status] || "Pharmacy delivery is queued."}</p>}
  </section>;
}
function BodyFigure({ gender, side }) {
  const female = gender === "female";
  const fill = side === "anterior" ? "#dbeafe" : "#e0e7ff";
  return <svg viewBox="0 0 200 450" className="absolute inset-0 h-full w-full pointer-events-none" role="img" aria-label={`${gender} ${side} body figure`}>
    <g fill={fill} stroke="#64748b" strokeWidth="2">
      <circle cx="100" cy="35" r="23" />
      <path d={female
        ? "M75 65 Q100 55 125 65 L136 145 Q126 178 123 210 L77 210 Q74 178 64 145 Z"
        : "M70 65 Q100 53 130 65 L137 145 L122 215 L78 215 L63 145 Z"} />
      <path d="M70 75 L47 95 L25 205 Q22 218 34 221 Q43 222 47 210 L73 125 Z" />
      <path d="M130 75 L153 95 L175 205 Q178 218 166 221 Q157 222 153 210 L127 125 Z" />
      <path d="M78 205 L96 205 L91 330 L78 430 Q76 443 64 441 Q54 438 58 425 L66 320 Z" />
      <path d="M104 205 L122 205 L134 320 L142 425 Q146 438 136 441 Q124 443 122 430 L109 330 Z" />
    </g>
    <path d="M100 60 L100 410" stroke="#94a3b8" strokeDasharray="4 7" opacity={side === "posterior" ? 0.65 : 0.2} />
  </svg>;
}

export function EncounterSummaryPage({ homeBase, links, mode = "doctor" }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const showBodyCharts = mode !== "patient" && !["patient", "guest"].includes(user?.role) && Boolean(user?.role);
  const { runSave, saving, saveError } = useEncounterSave();
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [activeTab, setActiveTab] = useState("clinical"); // clinical | body_chart | follow_up | activity
  
  const canEditPerm = useCan("encounters.edit");
  const canClosePerm = useCan("encounters.close");
  const canAddFollowupPerm = useCan("followups.create");
  const canAmendPerm = useCan("encounters.amend");
  const canEditPatientPerm = useCan("patients.edit");
  const canEditEncounterTags = useCan("encounters.edit");
  const canEditPatientTags = canEditPatientPerm || canEditEncounterTags;
  const canCreateInvoice = useCan("billing.create_invoice");

  const isReadOnly = mode === "patient" || mode === "admin";
  const isClosed = ["completed", "closed"].includes(String(data?.encounter?.status || "").toLowerCase()) || Boolean(data?.encounter?.closed_at);
  const canEdit = !isReadOnly && !isClosed && (canEditPerm || user.role === "doctor");
  const canClose = !isReadOnly && !isClosed && (canClosePerm || user.role === "doctor");
  const canAddFollowup = !isReadOnly && (canAddFollowupPerm || user.role === "doctor");
  const canAmend = !isReadOnly && isClosed && canAmendPerm;
  const [amendOpen, setAmendOpen] = useState(false);
  const [amendment, setAmendment] = useState({ reason: "", note: "" });
  const [patientTagIds, setPatientTagIds] = useState([]);
  const [savingTags, setSavingTags] = useState(false);

  // Clinical Tab State
  const [form, setForm] = useState({});
  const [savedClinical, setSavedClinical] = useState("");
  const [loadedDraftKey, setLoadedDraftKey] = useState(null);
  const clinicalDraftKey = draftKey(user, id);
  const [problems, setProblems] = useState([]);
  const [newProblem, setNewProblem] = useState("");
  const [observations, setObservations] = useState([]);
  const [newObs, setNewObs] = useState("");
  
  const [rx, setRx] = useState([]);
  const [rxNotes, setRxNotes] = useState("");
  const [sendingPrescriptionId, setSendingPrescriptionId] = useState("");
  
  // Body Chart State
  const [bodyCharts, setBodyCharts] = useState([]);
  const [patientHistoryCharts, setPatientHistoryCharts] = useState([]);
  const [currentChart, setCurrentChart] = useState({
    chart_name: "Initial Assessment",
    pain_score: 0,
    overall_severity: "Normal",
    figure_gender: "unspecified",
    regions: []
  });
  
  // Follow Up Chain State
  const [chainData, setChainData] = useState(null); // The specific treatment chain
  const [patientFollowups, setPatientFollowups] = useState([]); // Timeline
  const clinicalSnapshot = JSON.stringify({ form, problems, observations });
  const clinicalDirty = loadedDraftKey === clinicalDraftKey && canEdit && Boolean(savedClinical) && clinicalSnapshot !== savedClinical;
  useEffect(() => {
    if (loadedDraftKey !== clinicalDraftKey) return;
    if (clinicalDirty) rememberDraft(clinicalDraftKey, { form, problems, observations });
    else if (savedClinical) forgetDraft(clinicalDraftKey);
  }, [clinicalDirty, clinicalDraftKey, loadedDraftKey, clinicalSnapshot, savedClinical, form, problems, observations]);
  useEffect(() => {
    if (!clinicalDirty) return;
    const warn = event => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [clinicalDirty]);
  useEffect(()=>{
    if(!rx.some(item=>!item.prescription_id&&item.medicine_name))return;
    const warn=e=>{e.preventDefault();e.returnValue="";};
    window.addEventListener("beforeunload",warn);return()=>window.removeEventListener("beforeunload",warn);
  },[rx]);
  const [followupOpen, setFollowupOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError("");
      const r = await api.get(`/encounters/${id}`);
      setData(r.data);
      setPatientTagIds(r.data.patient?.tag_ids || []);
      const loadedForm = {
        chief_complaint: r.data.encounter.chief_complaint || "",
        symptoms: r.data.encounter.symptoms || "",
        diagnosis: r.data.encounter.diagnosis || "",
        clinical_notes: r.data.encounter.clinical_notes || "",
        vitals: normalizeEncounterVitals(r.data.encounter.vitals),
        status: r.data.encounter.status,
      };
      const loadedClinical = { form: loadedForm, problems: r.data.encounter.problems || [], observations: r.data.encounter.observations_list || [] };
      setSavedClinical(JSON.stringify(loadedClinical));
      setLoadedDraftKey(clinicalDraftKey);
      const completed = ["completed", "closed"].includes(String(r.data.encounter.status || "").toLowerCase()) || r.data.encounter.closed_at;
      const retained = !completed && !isReadOnly ? readDraft(clinicalDraftKey) : null;
      const display = retained || loadedClinical;
      setForm(display.form);
      setProblems(display.problems);
      setObservations(display.observations);
      if (retained) toast.info("Unsaved clinical draft restored in this browser. Review it and save to keep it.");
      if (completed) forgetDraft(clinicalDraftKey);
      setRx((r.data.prescriptions || []).flatMap((prescription) =>
        (prescription.items || prescription.medicines || []).map((item, index) => ({
          ...item,
          prescription_id: prescription.prescription_id,
          item_key: `${prescription.prescription_id}-${index}`,
          prescription_notes: prescription.notes,
        }))
      ));
      setChainData(r.data.chain || null);
      setCurrentChart((chart) => ({
        ...chart,
        figure_gender: ["male", "female"].includes(String(r.data.patient?.gender || "").toLowerCase())
          ? String(r.data.patient.gender).toLowerCase() : chart.figure_gender,
      }));
      
      // Load body charts
      if (showBodyCharts) api.get(`/encounters/${id}/body-charts`).then(res => setBodyCharts(res.data)).catch(()=>{});
      if (r.data.patient?.patient_id) {
        if (showBodyCharts) api.get(`/patients/${r.data.patient.patient_id}/body-charts`).then(res => setPatientHistoryCharts(res.data)).catch(()=>{});
        api.get(`/followups`, { params: { patient_id: r.data.patient.patient_id } }).then(res => setPatientFollowups(res.data)).catch(()=>{});
      }
      setLoading(false);
    } catch (e) {
      setLoadError(e?.response?.data?.detail || e?.message || "Failed to load encounter");
      setLoading(false);
    }
  }, [id, showBodyCharts, clinicalDraftKey, isReadOnly]);

  useEffect(() => { load(); }, [load]);

  const updatePatientTags = async (tagIds) => {
    if (!data?.patient?.patient_id || !canEditPatientTags || mode === "patient") return;
    const previous = patientTagIds;
    setPatientTagIds(tagIds);
    setSavingTags(true);
    try {
      const { data: result } = await api.put(`/clinic/patients-x/tag-assignments/${data.patient.patient_id}`, {
        tag_ids: tagIds,
        source: "encounter",
      });
      setPatientTagIds((result.tags || []).map((tag) => tag.tag_id));
      setData((current) => ({
        ...current,
        patient: { ...current.patient, tag_ids: tagIds, tags: result.tags || [] },
      }));
      toast.success("Patient tags updated");
    } catch (error) {
      setPatientTagIds(previous);
      toast.error(error?.response?.data?.detail || "Patient tags could not be updated");
    } finally {
      setSavingTags(false);
    }
  };

  const save = async () => {
    if (!canEdit) return;
    const submittedSnapshot = clinicalSnapshot;
    const saved = await runSave(async () => {
      const payload = {
        ...form,
        problems,
        observations_list: observations
      };
      await persistPrescription();
      await api.patch(`/encounters/${id}`, payload);
    });
    if (saved) { setSavedClinical(submittedSnapshot); toast.success("Clinical changes saved"); }
  };

  const updateVital = (field, value) => {
    setForm((current) => {
      const vitals = { ...(current.vitals || {}) };
      field.aliases.forEach((alias) => delete vitals[alias]);
      vitals[field.key] = value;
      return { ...current, vitals };
    });
  };

  const [closing, setClosing] = useState(false);
  
  const closeEncounter = async (chainOpts = {}) => {
    if (!canClose) return;
    const saved = await runSave(async () => {
      await persistPrescription();
      await api.patch(`/encounters/${id}`, { ...form, status: "Completed", treatment_chain_id: data.encounter.treatment_chain_id, problems, observations_list: observations });
    });
    if (saved) {
      forgetDraft(clinicalDraftKey);
      toast.success("Consultation completed");
      setClosing(false);
      load();
    }
  };

  const [prescriptionKey, setPrescriptionKey] = useState(()=>crypto.randomUUID());
  const [pharmacySubmissions, setPharmacySubmissions] = useState({});
  const persistPrescription = async () => {
    const newItems = rx.filter(r => !r.prescription_id && r.medicine_name?.trim());
    if (!newItems.length) return;
    const payloadItems = newItems.map(({ draft_id, pharmacy_unit_name, pharmacy_available_quantity, pharmacy_lot_number, pharmacy_expiry_date, pharmacy_selection_expires_at, ...item }) => item);
    const {data: result} = await api.post("/prescriptions", { encounter_id: id, items: payloadItems, notes: rxNotes, idempotency_key: prescriptionKey });
    setRx(current=>current.map(item=>newItems.includes(item)?{...item,prescription_id:result.prescription_id}:item));
    setPrescriptionKey(crypto.randomUUID());
  };
  const sendPrescriptionToPharmacy = async (prescription) => {
    if (!isCurrentTypedPrescription(prescription)) return;
    setSendingPrescriptionId(prescription.prescription_id);
    try {
      const response = await api.post(`/prescriptions/${encodeURIComponent(prescription.prescription_id)}/pharmacy-submissions`);
      setPharmacySubmissions((current) => ({ ...current, [prescription.prescription_id]: response.data }));
      toast.success("Prescription queued for pharmacy delivery");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Prescription could not be sent to pharmacy");
    } finally { setSendingPrescriptionId(""); }
  };
  useEffect(() => {
    let cancelled = false;
    (data?.prescriptions || []).forEach((prescription) => {
      api.get(`/prescriptions/${encodeURIComponent(prescription.prescription_id)}/pharmacy-submissions`)
        .then((response) => {
          if (!cancelled && response.data?.submission) {
            setPharmacySubmissions((current) => ({ ...current, [prescription.prescription_id]: response.data.submission }));
          }
        })
        .catch(() => {});
    });
    return () => { cancelled = true; };
  }, [data?.prescriptions]);
  const savePrescription = async () => {
    if(isReadOnly)return;
    if(await runSave(persistPrescription)) toast.success("Prescription saved");
  };

  const addProblem = () => {
    if(!newProblem.trim()) return;
    setProblems([...problems, newProblem.trim()]);
    setNewProblem("");
  };
  const removeProblem = (idx) => setProblems(problems.filter((_, i) => i !== idx));

  const addObservation = () => {
    if(!newObs.trim()) return;
    setObservations([...observations, newObs.trim()]);
    setNewObs("");
  };
  const removeObs = (idx) => setObservations(observations.filter((_, i) => i !== idx));

  const handleBodyChartClick = (e, side) => {
    // Very simplified generic click handler
    if (!canEdit || isReadOnly) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    const newRegion = {
      id: Date.now().toString(),
      x, y, side,
      name: "Marked Area",
      color: "red",
      notes: "Tenderness"
    };
    setCurrentChart(prev => ({...prev, regions: [...prev.regions, newRegion]}));
  };

  const saveBodyChart = async () => {
    if (isReadOnly) return;
    try {
      await api.post("/body-charts", {
        encounter_id: id,
        ...currentChart
      });
      toast.success("Body chart saved");
      api.get(`/encounters/${id}/body-charts`).then(res => setBodyCharts(res.data));
    } catch(e) { toast.error("Failed"); }
  };

  const getEncounterUrl = (encId) => {
    if (mode === "doctor") return `/doctor/encounter/${encId}`;
    return `${homeBase}/encounters/${encId}`;
  };

  const saveAmendment = async () => {
    try {
      await api.post(`/encounters/${id}/amendments`, amendment);
      toast.success("Amendment added to the audit trail");
      setAmendOpen(false); setAmendment({ reason: "", note: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add amendment"); }
  };

  const displayDoctor = (name) => !name ? "Assigned doctor" : (/^dr\.?\s/i.test(name) ? name : `Dr. ${name}`);

  if (loading) return <DashboardLayout links={links} title="Encounter Summary">Loading...</DashboardLayout>;
  if (loadError || !data) return <DashboardLayout links={links} title="Encounter Summary"><div className="mg-card text-center py-10"><p className="font-semibold text-red-700">{loadError || "Encounter could not be loaded"}</p><button type="button" className="mg-btn-primary mt-4" onClick={load}>Retry</button></div></DashboardLayout>;

  return (
    <DashboardLayout links={links} title="Encounter Summary">
      {saving && <p role="status" className="mb-4 text-sm text-slate-600">Saving clinical changes…</p>}
      {saveError && <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">{saveError}</div>}
      {clinicalDirty && <div role="status" className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">Unsaved clinical changes. Save before closing or refreshing this tab. If you navigate elsewhere in the application, this draft can be restored for this account for up to 30 minutes in the same tab; it is not yet saved to the patient record.</div>}
      {/* Top Action Bar */}
      <div className="flex justify-between items-center mb-6 pb-4 border-b border-slate-200">
         <div className="flex items-center gap-4">
           <h1 className="text-2xl font-bold text-mg-navy flex items-center gap-2">
             Encounter Summary <CheckCircle className="w-5 h-5 text-mg-health" />
           </h1>
         </div>
         <div className="flex gap-2">
           <button onClick={()=>{try{printEncounter(data,{...data.encounter,...form,problems,observations_list:observations},rx,rxNotes,patientFollowups);}catch(e){toast.error(e.message);}}} className="mg-btn-secondary"><Printer className="w-4 h-4"/> Print</button>
           {mode !== "patient" && canCreateInvoice && <button onClick={() => navigate(`${homeBase}/billing?encounter_id=${id}`)} className="mg-btn-secondary"><FileText className="w-4 h-4"/> Create invoice</button>}
           {data.encounter.status !== "Completed" && canClose && (
             <button onClick={() => setClosing(true)} className="mg-btn-primary bg-red-50 text-red-600 border-red-200 hover:bg-red-100 hover:border-red-300">
               <XCircle className="w-4 h-4"/> Complete consultation
             </button>
           )}
           {canAmend && <button onClick={() => setAmendOpen(true)} className="mg-btn-primary" data-testid="amend-encounter-btn"><Plus className="w-4 h-4" /> Add amendment</button>}
           <button onClick={() => navigate(-1)} className="mg-btn-secondary"><ChevronLeft className="w-4 h-4"/> Back</button>
         </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        
        {/* Left Column: Info */}
        <div className="lg:w-1/4 space-y-6">
          <div className="mg-card p-5">
             <div className="flex justify-between items-center mb-4">
               <h3 className="font-bold text-mg-navy text-sm uppercase tracking-wider">Patient Information</h3>
             </div>
             <div className="flex items-center gap-3 mb-4">
               <div className="w-12 h-12 bg-mg-medical/10 text-mg-medical rounded-full flex items-center justify-center font-bold text-lg">
                 {data.patient?.name?.substring(0,2).toUpperCase()}
               </div>
               <div>
                 <div className="font-bold text-mg-navy">{data.patient?.name}</div>
                 <div className="text-xs text-slate-500">{data.patient?.gender} • <span className="mg-status mg-status-active">Active</span></div>
               </div>
             </div>
             <div className="space-y-2 text-xs text-slate-600">
                <div className="flex items-center gap-2"><Phone className="w-3 h-3"/> {data.patient?.phone}</div>
                <div className="mt-4 pt-4 border-t border-slate-100" data-testid="encounter-patient-tags">
                  <div className="text-slate-400 mb-2">Patient tags {savingTags ? "· saving…" : ""}</div>
                  {canEditPatientTags && mode !== "patient" ? (
                    <PatientTagPicker value={patientTagIds} onChange={updatePatientTags} />
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {(data.patient?.tags || []).map((tag) => <span key={tag.tag_id} className="rounded-full border px-2 py-0.5 font-semibold" style={{ borderColor: tag.color, color: tag.color }}>{tag.display_name}</span>)}
                      {!(data.patient?.tags || []).length && <span className="text-slate-400">No tags</span>}
                    </div>
                  )}
                </div>
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <div className="text-slate-400 mb-1">Encounter Date</div>
                  <div className="font-semibold text-mg-navy">{new Date(data.encounter.encounter_datetime).toLocaleString()}</div>
                </div>
                <div className="mt-2">
                  <div className="text-slate-400 mb-1">Origin Branch</div>
                  <div className="font-semibold text-mg-navy">{data.origin_branch?.name || "Unknown branch"}</div>
                </div>
                <div className="mt-2">
                  <div className="text-slate-400 mb-1">Encounter ID</div>
                  <div className="font-mono text-mg-navy bg-slate-50 px-2 py-1 rounded inline-block">{data.encounter.encounter_id}</div>
                </div>
             </div>
          </div>

          <div className="mg-card p-5">
             <div className="flex justify-between items-center mb-4">
               <h3 className="font-bold text-mg-navy text-sm uppercase tracking-wider">Encounter Details</h3>
             </div>
             <div className="space-y-4 text-xs text-slate-600">
                <div>
                  <div className="text-slate-400 mb-1">Assigned Doctor</div>
                  <div className="font-semibold text-mg-navy">{displayDoctor(data.doctor?.name)}</div>
                </div>
                <div>
                  <div className="text-slate-400 mb-1">Description</div>
                  <div className="font-semibold text-mg-navy">{data.encounter.chief_complaint || "Routine Checkup"}</div>
                </div>
                <div>
                  <div className="text-slate-400 mb-1">Status</div>
                  <div className={`font-semibold ${data.encounter.status === 'Completed' ? 'text-mg-health' : 'text-mg-gold'}`}>{data.encounter.status}</div>
                </div>
             </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="lg:w-3/4 flex flex-col">
          {/* Tabs Navigation */}
          <div className="flex items-center border-b border-slate-200 mb-6 gap-6">
            <button onClick={()=>setActiveTab("clinical")} className={`pb-3 font-semibold text-sm transition-colors border-b-2 ${activeTab==="clinical" ? "border-mg-medical text-mg-medical" : "border-transparent text-slate-500 hover:text-mg-navy"}`}>
              <FileText className="w-4 h-4 inline-block mr-2" /> Clinical Details
            </button>
            {showBodyCharts && <button onClick={()=>setActiveTab("body_chart")} className={`pb-3 font-semibold text-sm transition-colors border-b-2 ${activeTab==="body_chart" ? "border-mg-medical text-mg-medical" : "border-transparent text-slate-500 hover:text-mg-navy"}`}>
              <Activity className="w-4 h-4 inline-block mr-2" /> Body Chart
            </button>}
            <button onClick={()=>setActiveTab("follow_up")} className={`pb-3 font-semibold text-sm transition-colors border-b-2 ${activeTab==="follow_up" ? "border-mg-medical text-mg-medical" : "border-transparent text-slate-500 hover:text-mg-navy"}`}>
              <Clock className="w-4 h-4 inline-block mr-2" /> Follow-Up Chain
            </button>
            <button onClick={()=>setActiveTab("activity")} className={`pb-3 font-semibold text-sm transition-colors border-b-2 ${activeTab==="activity" ? "border-mg-medical text-mg-medical" : "border-transparent text-slate-500 hover:text-mg-navy"}`}>
              <Activity className="w-4 h-4 inline-block mr-2" /> Activity Log
            </button>
          </div>

          {/* Tab Content */}
          <div className="flex-1">
            {(data.encounter.amendments || []).length > 0 && <div className="mg-card mb-5 border-amber-200 bg-amber-50"><h4 className="font-bold text-mg-navy mb-2">Signed amendments</h4>{data.encounter.amendments.map((a) => <div key={a.amendment_id} className="border-t border-amber-200 py-2 text-sm"><div className="font-semibold">{a.reason}</div><div>{a.note}</div><div className="text-xs text-slate-500 mt-1">{a.created_by_name || "Clinic user"} · {new Date(a.created_at).toLocaleString()}</div></div>)}</div>}
            {activeTab === "clinical" && (
              <div className="space-y-6">
                <div className="mg-card p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="text-xs font-bold text-slate-600">Chief complaint <span className="text-red-500">*</span>
                    <textarea className="mg-input w-full mt-1" rows={3} value={form.chief_complaint || ""} onChange={e=>setForm({...form, chief_complaint:e.target.value})} readOnly={!canEdit} placeholder="Primary reason for this consultation" />
                  </label>
                  <label className="text-xs font-bold text-slate-600">Symptoms and history
                    <textarea className="mg-input w-full mt-1" rows={3} value={form.symptoms || ""} onChange={e=>setForm({...form, symptoms:e.target.value})} readOnly={!canEdit} placeholder="Symptoms, duration and relevant history" />
                  </label>
                  <label className="text-xs font-bold text-slate-600 md:col-span-2">Diagnosis <span className="text-red-500">*</span>
                    <textarea className="mg-input w-full mt-1" rows={3} value={form.diagnosis || ""} onChange={e=>setForm({...form, diagnosis:e.target.value})} readOnly={!canEdit} placeholder="Working or confirmed diagnosis" />
                  </label>
                  {canEdit && <div className="md:col-span-2 flex justify-end"><button onClick={save} className="mg-btn-primary text-xs">Save clinical summary</button></div>}
                </div>

                <section className="mg-card p-5" aria-labelledby="encounter-vitals-heading" data-testid="encounter-vitals">
                  <div className="mb-4">
                    <h4 id="encounter-vitals-heading" className="font-bold text-mg-navy flex items-center gap-2">
                      <Activity className="w-4 h-4 text-mg-medical" aria-hidden="true" /> Vitals
                    </h4>
                    <p className="mt-1 text-xs text-slate-500">Measurements recorded during this consultation.</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                    {VITAL_FIELDS.map((field) => (
                      <label key={field.key} className="text-xs font-bold text-slate-600">
                        {field.label}
                        <div className="relative mt-1">
                          <input
                            aria-label={`${field.label} (${field.unit})`}
                            className="mg-input w-full pr-20"
                            type={field.type}
                            inputMode={field.inputMode || "decimal"}
                            min={field.min}
                            max={field.max}
                            step={field.step}
                            placeholder={field.placeholder}
                            value={form.vitals?.[field.key] ?? ""}
                            onChange={(event) => updateVital(field, event.target.value)}
                            readOnly={!canEdit}
                          />
                          <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-[11px] font-medium text-slate-400" aria-hidden="true">{field.unit}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                  {canEdit && (
                    <div className="mt-4 flex justify-end">
                      <button type="button" onClick={save} className="mg-btn-primary text-xs">Save vitals</button>
                    </div>
                  )}
                </section>
                
                {/* Problems & Observations */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="mg-card p-5 bg-white border border-slate-100 shadow-sm">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="font-bold text-mg-navy">Problems</h4>
                    </div>
                    {canEdit && (
                      <div className="flex mb-4">
                        <input className="mg-input text-sm rounded-r-none" placeholder="Search or type problem..." value={newProblem} onChange={e=>setNewProblem(e.target.value)} onKeyDown={e=>e.key==='Enter' && addProblem()} />
                        <button onClick={addProblem} className="mg-btn-primary rounded-l-none"><Plus className="w-4 h-4"/></button>
                      </div>
                    )}
                    <div className="space-y-2">
                      {problems.map((p, i) => (
                        <div key={i} className="flex justify-between items-center p-2 bg-slate-50 rounded text-sm">
                          <span className="flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-xs font-bold">{i+1}</span> {p}</span>
                          {canEdit && <button onClick={()=>removeProblem(i)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4"/></button>}
                        </div>
                      ))}
                      {problems.length === 0 && <div className="text-xs text-slate-400 text-center py-2">No problems listed</div>}
                    </div>
                  </div>

                  <div className="mg-card p-5 bg-white border border-slate-100 shadow-sm">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="font-bold text-mg-navy">Observations</h4>
                    </div>
                    {canEdit && (
                      <div className="flex mb-4">
                        <input className="mg-input text-sm rounded-r-none" placeholder="Search or type observation..." value={newObs} onChange={e=>setNewObs(e.target.value)} onKeyDown={e=>e.key==='Enter' && addObservation()} />
                        <button onClick={addObservation} className="mg-btn-primary rounded-l-none"><Plus className="w-4 h-4"/></button>
                      </div>
                    )}
                    <div className="space-y-2">
                      {observations.map((o, i) => (
                        <div key={i} className="flex justify-between items-center p-2 bg-slate-50 rounded text-sm">
                          <span className="flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-xs font-bold">{i+1}</span> {o}</span>
                          {canEdit && <button onClick={()=>removeObs(i)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4"/></button>}
                        </div>
                      ))}
                      {observations.length === 0 && <div className="text-xs text-slate-400 text-center py-2">No observations listed</div>}
                    </div>
                  </div>
                </div>

                {/* Notes Box */}
                <div className="mg-card p-0 overflow-hidden border border-slate-200">
                  <div className="p-4 border-b border-slate-100 bg-slate-50">
                    <h4 className="font-bold text-mg-navy">Clinical Notes</h4>
                  </div>
                  <div className="p-4">
                    <textarea 
                      className="w-full min-h-[150px] p-4 text-sm text-slate-700 outline-none resize-none placeholder:text-slate-300"
                      placeholder="Type your clinical notes here..."
                      value={form.clinical_notes}
                      onChange={e=>setForm({...form, clinical_notes: e.target.value})}
                      onBlur={save}
                      readOnly={!canEdit}
                    />
                  </div>
                  {canEdit && (
                    <div className="p-3 border-t border-slate-100 bg-slate-50 flex justify-end">
                      <button onClick={save} className="mg-btn-primary text-xs px-6 py-2">Save Note</button>
                    </div>
                  )}
                </div>
                
                {/* Uploaded Reports placeholder */}
                <div className="mg-card p-5 bg-white border border-slate-100 shadow-sm">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="font-bold text-mg-navy">Uploaded Reports</h4>
                    {canEdit && <button className="text-xs text-mg-medical font-semibold flex items-center gap-1"><Plus className="w-3 h-3"/> Add Report</button>}
                  </div>
                  <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 flex flex-col items-center justify-center text-slate-400 bg-slate-50/50">
                    <ImageIcon className="w-8 h-8 mb-2 text-slate-300" />
                    <p className="text-sm">No reports uploaded yet.</p>
                    <p className="text-xs">Add a report to attach files and documents.</p>
                  </div>
                </div>

                {/* Prescription */}
                <div className="mg-card p-5 bg-white border border-slate-100 shadow-sm">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="font-bold text-mg-navy">Prescription</h4>
                    {canEdit && <button onClick={() => setRx([...rx, { draft_id: crypto.randomUUID(), medicine_name: "", strength: "", dosage: "", frequency: "", duration: "", timing: "", instructions: "" }])} className="text-xs text-mg-medical font-semibold flex items-center gap-1"><Plus className="w-3 h-3"/> Add clinical medicine</button>}
                  </div>
                  {canEdit && <PharmacyProductPicker onAdd={(item) => setRx((current) => [...current, item])} />}
                  
                  {(data.prescriptions || []).length > 0 || rx.some((item) => !item.prescription_id) ? (
                    <div className="space-y-4">
                      {(data.prescriptions || []).map((prescription) => <SavedPrescriptionGroup key={prescription.prescription_id} prescription={prescription} canSend={canEdit} onSend={sendPrescriptionToPharmacy} sending={sendingPrescriptionId === prescription.prescription_id} submission={pharmacySubmissions[prescription.prescription_id]} />)}
                      {rx.some((item) => !item.prescription_id) && <>
                      <div className="space-y-3">
                        {rx.filter((item) => !item.prescription_id).map((r, idx) => (
                          <PrescriptionLineEditor
                            key={r.draft_id || idx}
                            item={r}
                            index={idx}
                            medicineEditor={<MedicationAutocomplete value={r.medicine_name} onChange={(value) => setRx((current) => current.map((item) => item === r ? { ...item, medicine_name: value } : item))}
                              onSelect={(medication) => setRx((current) => current.map((item) => item === r ? { ...item, medicine_name: medication.name,
                                strength: item.strength || medication.default_strength || "", dosage: item.dosage || medication.default_dosage || "",
                                frequency: item.frequency || medication.default_frequency || "", duration: item.duration || medication.default_duration || "",
                                instructions: item.instructions || medication.default_instructions || "" } : item))} />}
                            onChange={(field, value) => setRx((current) => current.map((item) => item === r ? { ...item, [field]: value } : item))}
                            onRemove={() => setRx((current) => current.filter((item) => item !== r))}
                          />
                        ))}
                      </div>
                      </>}
                      {canEdit && rx.some(r => !r.prescription_id) && (
                        <div className="flex justify-end pt-2">
                          <button onClick={savePrescription} className="mg-btn-primary text-xs">Save New Rx</button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-sm text-slate-500 text-center py-6">No prescriptions added.</div>
                  )}
                </div>

              </div>
            )}

            {showBodyCharts && activeTab === "body_chart" && (
              <div className="space-y-6">
                <div className="flex gap-4 mb-4">
                   <label className="text-xs font-semibold text-slate-600">Body figure
                     <select className="mg-input ml-2 py-1 text-xs" value={currentChart.figure_gender}
                       onChange={(e)=>setCurrentChart({...currentChart, figure_gender:e.target.value})} disabled={!canEdit}>
                       <option value="unspecified">Neutral</option><option value="female">Female</option><option value="male">Male</option>
                     </select>
                   </label>
                   <button type="button" onClick={()=>{const previous=bodyCharts[0]||patientHistoryCharts[0]; if(previous)setCurrentChart({...currentChart, figure_gender:previous.figure_gender||"unspecified", regions:previous.regions||[], pain_score:previous.pain_score||0, overall_severity:previous.overall_severity||"Normal"});}} disabled={!bodyCharts.length&&!patientHistoryCharts.length} className="mg-btn-secondary text-xs disabled:opacity-50">Compare Previous</button>
                   <button type="button" onClick={()=>setCurrentChart({...currentChart, regions:[], pain_score:0, overall_severity:"Normal"})} disabled={!canEdit} className="mg-btn-secondary text-xs disabled:opacity-50">Reset View</button>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                  {/* The body SVGs */}
                  <div className="xl:col-span-2 mg-card bg-slate-50 p-6 flex flex-col md:flex-row justify-around items-center min-h-[400px] border border-slate-200 shadow-inner relative">
                     <div className="text-center relative w-[200px] h-[450px]">
                       <span className="text-xs font-bold text-slate-500 uppercase absolute -top-6 left-0 right-0">Anterior (Front)</span>
                       <div onClick={(e) => handleBodyChartClick(e, "anterior")} className="w-full h-full relative cursor-crosshair" data-testid="body-chart-anterior">
                          <BodyFigure gender={currentChart.figure_gender} side="anterior" />
                          {currentChart.regions.filter(r=>r.side==='anterior').map(r => (
                            <div key={r.id} className="absolute w-4 h-4 rounded-full bg-red-500/80 shadow-[0_0_10px_rgba(239,68,68,0.8)] -ml-2 -mt-2 animate-pulse" style={{left: `${r.x}%`, top: `${r.y}%`}}></div>
                          ))}
                       </div>
                     </div>
                     <div className="text-center relative w-[200px] h-[450px] mt-12 md:mt-0">
                       <span className="text-xs font-bold text-slate-500 uppercase absolute -top-6 left-0 right-0">Posterior (Back)</span>
                       <div onClick={(e) => handleBodyChartClick(e, "posterior")} className="w-full h-full relative cursor-crosshair" data-testid="body-chart-posterior">
                          <BodyFigure gender={currentChart.figure_gender} side="posterior" />
                          {currentChart.regions.filter(r=>r.side==='posterior').map(r => (
                            <div key={r.id} className="absolute w-4 h-4 rounded-full bg-blue-500/80 shadow-[0_0_10px_rgba(59,130,246,0.8)] -ml-2 -mt-2 animate-pulse" style={{left: `${r.x}%`, top: `${r.y}%`}}></div>
                          ))}
                       </div>
                     </div>
                  </div>
                  
                  {/* Insights Sidebar */}
                  <div className="space-y-4">
                     <div className="mg-card p-5 bg-white border border-slate-100">
                        <h4 className="font-bold text-mg-navy mb-4 flex items-center justify-between">Body Chart Insights</h4>
                        <div className="mb-6">
                           <div className="text-xs text-slate-500 font-semibold mb-2">Pain Score</div>
                           <div className="flex items-center gap-3">
                             <input type="range" min="0" max="10" className="w-full accent-mg-medical" value={currentChart.pain_score} onChange={e=>setCurrentChart({...currentChart, pain_score: parseInt(e.target.value)})} disabled={!canEdit} />
                             <span className="font-bold text-xl text-red-500 bg-red-50 px-3 py-1 rounded-lg border border-red-100">{currentChart.pain_score}</span>
                           </div>
                           <div className="flex justify-between text-[10px] text-slate-400 mt-1 font-mono">
                             <span>0</span><span>2</span><span>4</span><span>6</span><span>8</span><span>10</span>
                           </div>
                           <div className="mt-4 flex justify-between items-center">
                             <span className="text-xs text-slate-500 font-semibold">Overall Severity</span>
                             <select className="mg-input py-1 text-xs w-32 border-orange-200 text-orange-600 bg-orange-50 font-bold" value={currentChart.overall_severity} onChange={e=>setCurrentChart({...currentChart, overall_severity: e.target.value})} disabled={!canEdit}>
                               <option value="Mild">Mild</option>
                               <option value="Moderate">Moderate</option>
                               <option value="Severe">Severe</option>
                             </select>
                           </div>
                        </div>

                        <div>
                           <div className="text-xs text-slate-500 font-semibold mb-3">Marked Regions ({currentChart.regions.length})</div>
                           <div className="space-y-2 max-h-[150px] overflow-y-auto pr-2 custom-scrollbar">
                             {currentChart.regions.map(r => (
                               <div key={r.id} className="p-2 border border-slate-100 rounded-lg text-xs flex items-start gap-2">
                                  <div className="w-2 h-2 rounded-full mt-1 flex-shrink-0" style={{backgroundColor: r.color}}></div>
                                  <div>
                                     <div className="font-semibold text-mg-navy">{r.name}</div>
                                     <div className="text-slate-500">{r.notes}</div>
                                  </div>
                               </div>
                             ))}
                             {currentChart.regions.length===0 && <div className="text-xs text-slate-400">No regions marked. Click on the body to mark a region.</div>}
                           </div>
                        </div>
                        
                        {canEdit && (
                          <button onClick={saveBodyChart} className="mg-btn-primary w-full mt-4 text-xs py-2">Save Chart Data</button>
                        )}
                     </div>
                  </div>
                </div>

                <div className="mg-card p-5 bg-white border border-slate-100">
                  <h4 className="font-bold text-mg-navy mb-4">Body Chart History</h4>
                  <table className="w-full text-sm text-left">
                    <thead className="text-xs text-slate-500 uppercase border-b border-slate-100">
                      <tr><th className="pb-3">Chart Name</th><th className="pb-3">Regions</th><th className="pb-3">Added By</th><th className="pb-3">Date</th></tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {bodyCharts.map(c => (
                        <tr key={c.chart_id} className="hover:bg-slate-50">
                          <td className="py-3 font-semibold text-mg-navy">{c.chart_name}</td>
                          <td><span className="bg-slate-100 px-2 py-1 rounded text-xs">{c.regions?.length || 0} marks</span></td>
                          <td>{c.doctor_name || "Doctor"}</td>
                          <td className="text-slate-500">{new Date(c.created_at).toLocaleString()}</td>
                        </tr>
                      ))}
                      {bodyCharts.length===0 && <tr><td colSpan={4} className="py-4 text-slate-400 text-center">No historical body charts</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === "follow_up" && (
              <div className="space-y-6">
                 {chainData?.encounters?.length > 0 && <div className="mg-card border border-blue-100">
                   <h4 className="font-bold text-mg-navy">Treatment encounter timeline</h4>
                   <div className="relative mt-5 ml-3 space-y-5 border-l-2 border-blue-100">
                     {chainData.encounters.map((encounter) => <div key={encounter.encounter_id} className="relative pl-6">
                       <span className="absolute -left-2 top-1 h-3.5 w-3.5 rounded-full border-2 border-white bg-mg-medical" />
                       <button type="button" onClick={() => navigate(getEncounterUrl(encounter.encounter_id))} className="text-left">
                         <span className="block text-sm font-semibold text-mg-navy">{encounter.chief_complaint || "Clinical encounter"}</span>
                         <span className="block text-xs text-slate-500">{encounter.encounter_datetime ? new Date(encounter.encounter_datetime).toLocaleString() : "Date not recorded"} · {encounter.branch_name || "Unknown branch"}</span>
                       </button>
                     </div>)}
                   </div>
                 </div>}
                 <div className="flex justify-between items-center p-4 bg-blue-50/50 border border-blue-100 rounded-xl">
                   <div className="flex items-center gap-3">
                     <span className="font-bold text-mg-navy text-lg">Follow-Up Chain Summary</span>
                     <span className="mg-status mg-status-active">Active</span>
                     <span className="text-xs text-slate-500 font-semibold">{patientFollowups.filter(f=>f.status!=='Completed').length} active chain(s)</span>
                   </div>
                   {canAddFollowup && (
                     <div className="flex gap-2">
                       <button onClick={() => setFollowupOpen(true)} className="mg-btn-primary text-xs"><Plus className="w-3 h-3"/> Add Follow-Up</button>
                     </div>
                   )}
                 </div>

                 <div className="mg-card p-0 overflow-hidden border border-slate-200">
                    <div className="p-4 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
                       <div className="flex items-center gap-2 text-sm font-semibold text-mg-navy">
                         <Activity className="w-4 h-4 text-mg-medical"/> Active Treatment Chain
                       </div>
                       <button onClick={() => chainData && document.getElementById("chain-details")?.scrollIntoView({ behavior: "smooth" })} disabled={!chainData} className="text-xs font-semibold text-mg-medical hover:underline flex items-center gap-1 disabled:text-slate-400 disabled:no-underline">{chainData ? "View Chain Details" : "No chain linked"}</button>
                    </div>
                    
                    <div className="p-6">
                      <div className="relative border-l-2 border-slate-200 ml-4 space-y-8">
                        {patientFollowups.length === 0 && (
                          <div className="text-center text-slate-400 text-sm py-4">No follow-ups recorded for this patient.</div>
                        )}
                        
                        {patientFollowups.map((f, idx) => (
                          <div key={idx} className="relative pl-8">
                            {/* Node icon */}
                            <div className={`absolute -left-3.5 top-0 w-7 h-7 rounded-full flex items-center justify-center border-2 border-white shadow-sm ${f.status === 'Completed' ? 'bg-green-100 text-green-600' : 'bg-orange-100 text-orange-500'}`}>
                               {f.status === 'Completed' ? <CheckCircle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                            </div>
                            
                            <div className="bg-white border border-slate-100 shadow-sm rounded-xl p-4 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 hover:border-mg-medical transition-colors">
                              <div>
                                <div className="flex items-center gap-3 mb-1">
                                  <span className="font-bold text-mg-navy">Scheduled Follow-Up</span>
                                  <span className={`mg-status mg-status-${f.status?.toLowerCase()}`}>{f.status}</span>
                                </div>
                                <div className="text-xs text-slate-500 flex items-center gap-4">
                                  <span className="flex items-center gap-1"><Calendar className="w-3 h-3"/> {new Date(f.recommended_date).toLocaleDateString()}</span>
                                  <span className="flex items-center gap-1"><User className="w-3 h-3"/> {displayDoctor(f.doctor_name || data.doctor?.name)}</span>
                                </div>
                              </div>
                              <div className="flex-1 md:px-6 border-l border-slate-100">
                                <div className="text-xs text-slate-400 mb-1">Reason</div>
                                <div className="text-sm font-semibold text-mg-navy">{f.reason || "Routine check"}</div>
                              </div>
                              <div className="flex flex-col gap-2 min-w-[150px]">
                                 <button onClick={() => navigate(getEncounterUrl(f.previous_encounter_id || f.encounter_id || id))} className="text-xs font-semibold text-mg-medical flex items-center justify-end gap-1 hover:underline group">
                                   Open Encounter <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                                 </button>
                              </div>
                            </div>
                          </div>
                        ))}

                      </div>
                    </div>
                 </div>
                 {chainData && <div id="chain-details" className="mg-card p-5 border border-blue-100">
                   <div className="flex justify-between gap-4"><div><h4 className="font-bold text-mg-navy">{chainData.title}</h4><p className="text-sm text-slate-600 mt-1">{chainData.main_problem || chainData.reason || "No treatment objective recorded."}</p></div><span className={`mg-status mg-status-${String(chainData.status || "active").toLowerCase()}`}>{chainData.status || "Active"}</span></div>
                   <div className="grid grid-cols-2 gap-4 mt-4 text-xs text-slate-500"><div>Started<br/><strong className="text-mg-navy">{chainData.start_date ? new Date(chainData.start_date).toLocaleDateString() : "Not recorded"}</strong></div><div>Lead doctor<br/><strong className="text-mg-navy">{displayDoctor(chainData.doctor_name || data.doctor?.name)}</strong></div></div>
                 </div>}
              </div>
            )}
            {activeTab === "activity" && <div className="mg-card" data-testid="encounter-activity-log">
              <h4 className="font-bold text-mg-navy">Encounter activity</h4>
              <p className="mt-1 text-sm text-slate-500">A read-only history of changes made to this encounter.</p>
              <ol className="relative mt-6 ml-3 space-y-6 border-l-2 border-slate-200">
                {(data.activity || []).map((entry, index) => {
                  const changed = entry.metadata?.changed_fields || entry.changed_fields || entry.metadata?.fields || [];
                  const changedLabel = Array.isArray(changed) ? changed.join(", ") : (typeof changed === "object" ? Object.keys(changed).join(", ") : changed);
                  return <li key={entry.audit_id || `${entry.created_at}-${index}`} className="relative pl-7">
                    <span className="absolute -left-2 top-1 h-3.5 w-3.5 rounded-full border-2 border-white bg-mg-medical" />
                    <div className="text-sm font-semibold text-mg-navy">{String(entry.action || "activity").replaceAll("_", " ")}</div>
                    <div className="mt-1 text-xs text-slate-500">{entry.actor_name || "System"} · {entry.created_at ? new Date(entry.created_at).toLocaleString() : "Time not recorded"} · {entry.branch_name || "Platform"}</div>
                    {changedLabel && <div className="mt-1 text-xs text-slate-600">Changed fields: {changedLabel}</div>}
                  </li>;
                })}
                {!(data.activity || []).length && <li className="pl-7 text-sm text-slate-400">No activity has been recorded yet.</li>}
              </ol>
            </div>}
          </div>
        </div>

      </div>
      {amendOpen && <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4" role="dialog" aria-modal="true"><div className="bg-white rounded-2xl p-6 max-w-lg w-full"><h3 className="font-bold text-lg text-mg-navy mb-2">Add signed amendment</h3><p className="text-sm text-slate-500 mb-4">The original closed encounter will not be changed.</p><input className="mg-input w-full mb-3" placeholder="Reason for amendment" value={amendment.reason} onChange={(e) => setAmendment({...amendment, reason: e.target.value})} /><textarea className="mg-input w-full" rows={5} placeholder="Additional clinical note" value={amendment.note} onChange={(e) => setAmendment({...amendment, note: e.target.value})} /><div className="flex justify-end gap-2 mt-4"><button className="mg-btn-secondary" onClick={() => setAmendOpen(false)}>Cancel</button><button className="mg-btn-primary" disabled={!amendment.reason.trim() || !amendment.note.trim()} onClick={saveAmendment}>Sign amendment</button></div></div></div>}

      {closing && (
        <CloseEncounterModal 
          onClose={() => setClosing(false)}
          onConfirm={closeEncounter}
          currentChain={chainData}
        />
      )}
      {followupOpen && <AddEncounterFollowupModal encounter={data.encounter} chain={chainData} onClose={() => setFollowupOpen(false)} onSaved={() => { setFollowupOpen(false); load(); }} />}
    </DashboardLayout>
  );
}

function AddEncounterFollowupModal({ encounter, chain, onClose, onSaved }) {
  return <ScheduleFollowupModal patient={{ patient_id: encounter.patient_id }} doctorId={encounter.doctor_id}
    encounterId={encounter.encounter_id} chainId={chain?.chain_id || encounter.treatment_chain_id || null}
    onClose={onClose} onSaved={onSaved} />;
}

function CloseEncounterModal({ onClose, onConfirm }) {
  return <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Complete consultation"><div className="mg-card max-w-md"><h3 className="font-bold text-lg">Complete consultation?</h3><p className="my-4 text-sm">Clinical details and new prescriptions will be saved. The existing treatment chain stays linked. Further clinical changes require an amendment. Invoice issuance and payment can be completed separately.</p><div className="flex gap-3"><button className="mg-btn-secondary" onClick={onClose}>Continue editing</button><button className="mg-btn-primary" onClick={()=>onConfirm()}>Complete consultation</button></div></div></div>;
}
